import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Edit, Trash2, Package, Star, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    package_type: "class_pack",
    price: 5000,
    original_price: "",
    credits: 5,
    validity_days: 30,
    is_active: true,
    is_featured: false,
    benefits: "",
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data.packages || res.data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      package_type: "class_pack",
      price: 5000,
      original_price: "",
      credits: 5,
      validity_days: 30,
      is_active: true,
      is_featured: false,
      benefits: "",
    });
    setEditingPackage(null);
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name || "",
        slug: pkg.slug || "",
        description: pkg.description || "",
        package_type: pkg.package_type || "class_pack",
        price: pkg.price || 5000,
        original_price: pkg.original_price || "",
        credits: pkg.credits || 5,
        validity_days: pkg.validity_days || 30,
        is_active: pkg.is_active !== false,
        is_featured: pkg.is_featured || false,
        benefits: pkg.benefits || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, "-");
      const payload = {
        ...formData,
        slug,
        price: Number(formData.price),
        original_price: formData.original_price ? Number(formData.original_price) : null,
        credits: Number(formData.credits),
        validity_days: Number(formData.validity_days),
      };

      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.id}`, payload);
        toast.success("Package updated successfully!");
      } else {
        await axios.post(`${API}/admin/packages`, payload);
        toast.success("Package created successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save package");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (packageId, packageName) => {
    toast.warning(`Delete "${packageName}"?`, {
      description: "This will permanently remove the package. This cannot be undone.",
      action: {
        label: "Delete",
        onClick: async () => {
          try {
            await axios.delete(`${API}/admin/packages/${packageId}`);
            toast.success("Package deleted");
            fetchPackages();
          } catch {
            toast.error("Failed to delete package");
          }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const handleToggleActive = (pkg) => {
    if (pkg.is_active) {
      toast.warning(`Deactivate "${pkg.name}"?`, {
        description: "This package will no longer be available for purchase.",
        action: {
          label: "Deactivate",
          onClick: async () => {
            try {
              await axios.put(`${API}/admin/packages/${pkg.id}`, { is_active: false });
              toast.success("Package deactivated");
              fetchPackages();
            } catch {
              toast.error("Failed to update package");
            }
          },
        },
        cancel: { label: "Cancel", onClick: () => {} },
      });
      return;
    }
    axios.put(`${API}/admin/packages/${pkg.id}`, { is_active: true })
      .then(() => { toast.success("Package activated"); fetchPackages(); })
      .catch(() => toast.error("Failed to update package"));
  };

  const calculateDiscount = (pkg) => {
    if (pkg.original_price && pkg.original_price > pkg.price) {
      return Math.round((1 - pkg.price / pkg.original_price) * 100);
    }
    return 0;
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Packages</h1>
          <p className="text-neutral-400">Manage class packages and pricing</p>
        </div>
        
        <Button className="btn-empire" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Package
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{packages.length}</div>
            <p className="text-sm text-neutral-400">Total Packages</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {packages.filter(p => p.is_active).length}
            </div>
            <p className="text-sm text-neutral-400">Active</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              {packages.filter(p => p.is_featured).length}
            </div>
            <p className="text-sm text-neutral-400">Featured</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-400">
              {packages.filter(p => calculateDiscount(p) > 0).length}
            </div>
            <p className="text-sm text-neutral-400">With Discount</p>
          </CardContent>
        </Card>
      </div>

      {/* Active/Inactive Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Active ({packages.filter(p => p.is_active !== false).length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Inactive ({packages.filter(p => p.is_active === false).length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Packages Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : (() => {
        const filteredPackages = activeTab === "active"
          ? packages.filter(p => p.is_active !== false)
          : packages.filter(p => p.is_active === false);
        
        return filteredPackages.length === 0 ? (
          <Card className="card-empire">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p className="text-neutral-500">
                {activeTab === "active" ? "No active packages. Create your first package!" : "No inactive packages."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPackages.map((pkg, index) => {
              const discount = calculateDiscount(pkg);
              
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`card-empire relative ${!pkg.is_active ? "opacity-60" : ""}`}>
                    {pkg.is_featured && (
                      <Badge className="absolute -top-2 left-4 bg-empire-gold text-black">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Featured
                      </Badge>
                    )}
                    
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                        <CardTitle className="text-white text-lg">{pkg.name}</CardTitle>
                        <p className="text-sm text-neutral-400 mt-1">{pkg.description}</p>
                      </div>
                      <Badge variant={pkg.is_active ? "default" : "secondary"} className={pkg.is_active ? "bg-green-500/20 text-green-400" : ""}>
                        {pkg.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Price */}
                    <div>
                      {discount > 0 && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-neutral-500 line-through text-sm">
                            KES {pkg.original_price?.toLocaleString()}
                          </span>
                          <Badge className="bg-green-500/20 text-green-400 text-xs">
                            -{discount}%
                          </Badge>
                        </div>
                      )}
                      <span className="text-2xl font-bold text-empire-gold">
                        KES {pkg.price?.toLocaleString()}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-neutral-300">
                        <Check className="w-4 h-4 text-empire-gold" />
                        <span>{pkg.credits === 999 ? "Unlimited" : pkg.credits} classes</span>
                      </div>
                      <div className="flex items-center gap-2 text-neutral-300">
                        <Check className="w-4 h-4 text-empire-gold" />
                        <span>Valid for {pkg.validity_days} days</span>
                      </div>
                      {pkg.credits !== 999 && (
                        <div className="flex items-center gap-2 text-neutral-400">
                          <span>= KES {Math.round(pkg.price / pkg.credits).toLocaleString()} per class</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pkg.is_active}
                          onCheckedChange={() => handleToggleActive(pkg)}
                        />
                        <span className="text-xs text-neutral-500">Active</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(pkg)}
                          className="text-neutral-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(pkg.id, pkg.name)}
                          className="text-neutral-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
        );
      })()}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPackage ? "Edit Package" : "Create New Package"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Package Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-empire"
                placeholder="e.g., 5-Class Pack"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-empire min-h-[60px]"
                placeholder="Package description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Package Type</Label>
                <Select
                  value={formData.package_type}
                  onValueChange={(value) => setFormData({ ...formData, package_type: value })}
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="class_pack">Class Pack</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                    <SelectItem value="membership">Membership</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Credits (Classes)</Label>
                <Input
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                  className="input-empire"
                  placeholder="Use 999 for unlimited"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Price (KES) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="input-empire"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Original Price (for discount)</Label>
                <Input
                  type="number"
                  value={formData.original_price}
                  onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                  className="input-empire"
                  placeholder="Leave blank if no discount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Validity (Days)</Label>
              <Input
                type="number"
                value={formData.validity_days}
                onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                className="input-empire"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Benefits (one per line)</Label>
              <Textarea
                value={formData.benefits}
                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                className="input-empire min-h-[80px]"
                placeholder="Priority booking&#10;All class types included&#10;Free merchandise"
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label className="text-white">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                />
                <Label className="text-white">Featured</Label>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setDialogOpen(false); }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingPackage ? "Update Package" : "Create Package"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
